import React, { useEffect, useState } from "react";
import Title from "../../extra/Title";
import Button from "../../extra/Button";
import Pagination from "../../extra/Pagination";
import { useDispatch, useSelector } from "react-redux";
import { OPEN_DIALOGUE } from "../../store/dialogue/dialogue.type";
import CashoutDialog from "./CashoutDialog";
import { getCashoutRequest, getCashoutStats } from "../../store/cashoutRequest/cashoutRequest.action";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import dayjs from "dayjs";

const CashoutRequest = () => {
  const dispatch = useDispatch();
  const { cashoutRequest, stats } = useSelector((state) => state.cashoutRequest);
  const { dialogue, dialogueType } = useSelector((state) => state.dialogue);

  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    dispatch(getCashoutRequest(statusFilter));
    dispatch(getCashoutStats());
    setTimeout(() => {
      setIsLoading(false);
    }, 1500);
  }, [dispatch, statusFilter]);

  useEffect(() => {
    setData(cashoutRequest);
  }, [cashoutRequest]);

  const handleOpenDialog = (data) => {
    // The dialog reducer reads payload.data. Passing the row directly left the
    // dialog empty: Edit opened a blank form and Save CREATED a new package
    // (2026-10-04, boss: "coin prices are not editable").
    dispatch({ type: OPEN_DIALOGUE, payload: { type: "cashoutRequest", data } });
  };

  const indexOfLastPost = currentPage * rowsPerPage;
  const indexOfFirstPost = indexOfLastPost - rowsPerPage;
  const currentPosts = data.slice(indexOfFirstPost, indexOfLastPost);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const getStatusBadge = (status) => {
    const badges = {
      pending: "bg-warning text-dark",
      processing: "bg-info",
      completed: "bg-success",
      rejected: "bg-danger",
      cancelled: "bg-secondary",
    };
    return badges[status] || "bg-secondary";
  };

  return (
    <>
      <div className="mainCategory">
        <Title name="Cashout Requests" />

        {/* Statistics Cards */}
        <div className="row mb-4 mt-3">
          <div className="col-12 col-md-3 mb-3">
            <div className="card bg-gradient-warning text-white">
              <div className="card-body">
                <h6 className="card-subtitle mb-2 opacity-75">Pending Requests</h6>
                <h3 className="card-title mb-0">
                  {isLoading ? <Skeleton width={50} /> : stats.pendingCount || 0}
                </h3>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-3 mb-3">
            <div className="card bg-gradient-success text-white">
              <div className="card-body">
                <h6 className="card-subtitle mb-2 opacity-75">Completed Requests</h6>
                <h3 className="card-title mb-0">
                  {isLoading ? <Skeleton width={50} /> : stats.completedCount || 0}
                </h3>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-3 mb-3">
            <div className="card bg-gradient-info text-white">
              <div className="card-body">
                <h6 className="card-subtitle mb-2 opacity-75">Total Paid Out (USD)</h6>
                <h3 className="card-title mb-0">
                  {isLoading ? <Skeleton width={80} /> : (stats.totalPaidOut || 0).toFixed(2)}
                </h3>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-3 mb-3">
            <div className="card bg-gradient-primary text-white">
              <div className="card-body">
                <h6 className="card-subtitle mb-2 opacity-75">Pending Amount (USD)</h6>
                <h3 className="card-title mb-0">
                  {isLoading ? <Skeleton width={80} /> : (stats.pendingAmount || 0).toFixed(2)}
                </h3>
              </div>
            </div>
          </div>
        </div>

        {/* Filter */}
        <div className="row mb-3">
          <div className="col-12 col-md-4">
            <select
              className="form-control"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                backgroundColor: "#2d2d2d",
                color: "white",
                border: "1px solid #444",
                padding: "10px"
              }}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        <div className="row">
          <div className="col-12 col-md-12 mt-3">
            <div className="customTable">
              <div className="primeMain">
                <table width="100%" className="primeTable text-center">
                  <thead className="sticky-top" style={{ top: "-1px", zIndex: "1" }}>
                    <tr>
                      <th className="fw-bold py-3" style={{ width: "4%" }}>No</th>
                      <th className="fw-bold py-3" style={{ width: "10%" }}>Date</th>
                      <th className="fw-bold py-3" style={{ width: "15%" }}>Creator</th>
                      <th className="fw-bold py-3" style={{ width: "9%" }}>Coins</th>
                      <th className="fw-bold py-3" style={{ width: "10%" }}>Amount (USD)</th>
                      <th className="fw-bold py-3" style={{ width: "8%" }}>Fee</th>
                      <th className="fw-bold py-3" style={{ width: "10%" }}>Net Amount</th>
                      <th className="fw-bold py-3" style={{ width: "15%" }}>Bank Details</th>
                      <th className="fw-bold py-3" style={{ width: "10%" }}>Status</th>
                      <th className="fw-bold py-3" style={{ width: "9%" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <>
                        {Array.from({ length: 10 }).map((_, index) => (
                          <tr key={index}>
                            <td><Skeleton /></td>
                            <td><Skeleton /></td>
                            <td><Skeleton /></td>
                            <td><Skeleton /></td>
                            <td><Skeleton /></td>
                            <td><Skeleton /></td>
                            <td><Skeleton /></td>
                            <td><Skeleton /></td>
                            <td><Skeleton /></td>
                            <td><Skeleton /></td>
                          </tr>
                        ))}
                      </>
                    ) : (
                      <>
                        {currentPosts.length > 0 ? (
                          <>
                            {currentPosts.map((data, index) => {
                              return (
                                <tr key={index}>
                                  <td className="text-white">{indexOfFirstPost + index + 1}</td>
                                  <td>
                                    <div style={{ fontSize: "12px" }} className="text-white">
                                      {dayjs(data?.createdAt).format("DD/MM/YY")}
                                      <br />
                                      <span className="text-muted">
                                        {dayjs(data?.createdAt).format("HH:mm")}
                                      </span>
                                    </div>
                                  </td>
                                  <td>
                                    <div style={{ fontSize: "13px" }} className="text-white">
                                      {data?.userId?.name || data?.userId?.shopName || "Unknown"}
                                      <br />
                                      <span className="text-muted" style={{ fontSize: "11px" }}>
                                        {data?.userType}
                                      </span>
                                    </div>
                                  </td>
                                  <td>
                                    <span className="badge bg-info">
                                      {data?.coinAmount}
                                    </span>
                                  </td>
                                  <td>
                                    <span className="fw-bold text-white">
                                      ${data?.requestedAmount?.toFixed(2)}
                                    </span>
                                  </td>
                                  <td>
                                    <span className="text-danger">
                                      -{data?.processingFee?.toFixed(2)}
                                    </span>
                                    <br />
                                    <span className="text-muted" style={{ fontSize: "11px" }}>
                                      5%
                                    </span>
                                  </td>
                                  <td>
                                    <span className="fw-bold text-success">
                                      ${data?.finalAmount?.toFixed(2)}
                                    </span>
                                  </td>
                                  <td>
                                    <div style={{ fontSize: "12px" }} className="text-white">
                                      <strong>{data?.bankDetails?.bankName}</strong>
                                      <br />
                                      <span className="text-muted">
                                        {data?.bankDetails?.accountNumber}
                                      </span>
                                      <br />
                                      <span className="text-muted">
                                        {data?.bankDetails?.accountName}
                                      </span>
                                    </div>
                                  </td>
                                  <td>
                                    <span className={`badge ${getStatusBadge(data?.status)}`}>
                                      {data?.status}
                                    </span>
                                    {data?.status === "rejected" && data?.rejectionReason && (
                                      <div className="text-muted" style={{ fontSize: "10px" }}>
                                        {data?.rejectionReason}
                                      </div>
                                    )}
                                  </td>
                                  <td>
                                    {data?.status === "pending" || data?.status === "processing" ? (
                                      <Button
                                        newClass={`boxCenter userBtn fs-5 text-white`}
                                        btnColor={`bg-primary`}
                                        btnIcon={`fa-solid fa-eye`}
                                        onClick={() => handleOpenDialog(data)}
                                        style={{ borderRadius: "5px" }}
                                      />
                                    ) : (
                                      <span className="text-muted" style={{ fontSize: "11px" }}>
                                        {data?.status === "completed" ? "Processed" : "-"}
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </>
                        ) : (
                          <tr>
                            <td colSpan="10" className="text-center text-white">
                              No Cashout Requests Found
                            </td>
                          </tr>
                        )}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Was wired with props the shared control does not have
                (totalPosts/paginate/currentPage), so it never rendered and only
                the first page of rows was reachable (2026-09-30). */}
            <Pagination
              component="div"
              type={"client"}
              count={data.length}
              totalData={data.length}
              serverPage={currentPage - 1}
              serverPerPage={rowsPerPage}
              onPageChange={(_e, p) => setCurrentPage(p + 1)}
              onRowsPerPageChange={(v) => {
                setRowsPerPage(parseInt(v, 10) || 25);
                setCurrentPage(1);
              }}
              rowsPerPageOptions={[25, 50, 100, 500]}
            />
          </div>
        </div>
      </div>
      {/* only this page's dialog, not e.g. the header Notification one */}
      {dialogue && dialogueType === "cashoutRequest" && <CashoutDialog />}
    </>
  );
};

export default CashoutRequest;
